import React from 'react';
import { Airstrip, ExtraServiceDefinition } from '../../types';
import { AirstripImportSummaryModal } from './AirstripImportSummaryModal';
import { extraServicesApi } from '../../services/extraServices';

type AirstripsTabProps = {
  airstrips: Airstrip[];
  showAirstripModal: boolean;
  editingAirstrip: Airstrip | null;
  airstripForm: Partial<Airstrip>;
  setAirstripForm: (next: Partial<Airstrip>) => void;
  isSavingAirstrip: boolean;
  isImporting: boolean;
  importSummary: {
    created: number;
    updated: number;
    skipped: number;
    processed: number;
    errors: Array<{ row: number; message: string }>;
  } | null;
  onOpenModal: (airstrip?: Airstrip) => void;
  onCloseModal: () => void;
  onSave: () => void;
  onDelete: (code: string) => void;
  onDeleteAll: () => void;
  onDownloadTemplate: () => void;
  onImportFile: (file: File) => void;
  onCloseImportSummary: () => void;
};

export const AirstripsTab: React.FC<AirstripsTabProps> = ({
  airstrips,
  showAirstripModal,
  editingAirstrip,
  airstripForm,
  setAirstripForm,
  isSavingAirstrip,
  isImporting,
  importSummary,
  onOpenModal,
  onCloseModal,
  onSave,
  onDelete,
  onDeleteAll,
  onDownloadTemplate,
  onImportFile,
  onCloseImportSummary,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = React.useState('');
  const [serviceDefinitions, setServiceDefinitions] = React.useState<ExtraServiceDefinition[]>([]);
  const [servicePriceById, setServicePriceById] = React.useState<Record<string, string>>({});
  const [savingServicePrices, setSavingServicePrices] = React.useState(false);
  const [showGlobalServiceModal, setShowGlobalServiceModal] = React.useState(false);
  const [globalServiceName, setGlobalServiceName] = React.useState('');
  const [globalServiceCode, setGlobalServiceCode] = React.useState('');
  const [globalServiceScopeMode, setGlobalServiceScopeMode] = React.useState<'ORIGIN' | 'DESTINATION' | 'BOTH'>('BOTH');
  const [globalServicePrice, setGlobalServicePrice] = React.useState('0');
  const [globalSelectedAirstripCodes, setGlobalSelectedAirstripCodes] = React.useState<string[]>([]);
  const [globalAirstripSearch, setGlobalAirstripSearch] = React.useState('');
  const [creatingGlobalService, setCreatingGlobalService] = React.useState(false);
  const [manualServiceName, setManualServiceName] = React.useState('');
  const [manualServiceCode, setManualServiceCode] = React.useState('');
  const [manualServiceScopeMode, setManualServiceScopeMode] = React.useState<'ORIGIN' | 'DESTINATION' | 'BOTH'>('BOTH');
  const [manualServicePrice, setManualServicePrice] = React.useState('0');
  const normalizedQuery = query.trim().toLowerCase();
  const filteredAirstrips = normalizedQuery
    ? airstrips.filter(as => {
        const haystack = [as.code, as.icao, as.name, as.country]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedQuery);
      })
    : airstrips;
  const normalizedGlobalAirstripSearch = globalAirstripSearch.trim().toLowerCase();
  const globalFilteredAirstrips = normalizedGlobalAirstripSearch
    ? airstrips.filter(as =>
        [as.code, as.icao, as.name, as.country]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(normalizedGlobalAirstripSearch)
      )
    : airstrips;

  React.useEffect(() => {
    if (!showAirstripModal || !editingAirstrip?.code) return;
    let cancelled = false;
    const loadServices = async () => {
      try {
        const [definitions, configs] = await Promise.all([
          extraServicesApi.listDefinitions(true),
          extraServicesApi.listAirstripConfigs([editingAirstrip.code]),
        ]);
        if (cancelled) return;
        setServiceDefinitions(definitions.filter(item => item.scope === 'ORIGIN' || item.scope === 'DESTINATION'));
        const map: Record<string, string> = {};
        configs.forEach(cfg => {
          map[cfg.serviceId] =
            cfg.priceOverride === null || cfg.priceOverride === undefined ? '' : String(cfg.priceOverride);
        });
        setServicePriceById(map);
      } catch (error) {
        console.error('Failed to load airstrip service prices:', error);
      }
    };
    loadServices();
    return () => {
      cancelled = true;
    };
  }, [showAirstripModal, editingAirstrip?.code]);

  const saveAirstripServicePricing = async () => {
    if (!editingAirstrip?.code) return;
    setSavingServicePrices(true);
    try {
      await Promise.all(
        serviceDefinitions.map(def =>
          extraServicesApi.bulkUpsertAirstripConfig({
            serviceId: def.id,
            airstripCodes: [editingAirstrip.code],
            enabled: true,
            priceOverride:
              servicePriceById[def.id] === '' || servicePriceById[def.id] === undefined
                ? null
                : Number(servicePriceById[def.id]),
          })
        )
      );
      alert('Airstrip extra-service pricing saved.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to save extra-service pricing.');
    } finally {
      setSavingServicePrices(false);
    }
  };

  const toCode = (value: string) =>
    value
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  const scopeTargets = (mode: 'ORIGIN' | 'DESTINATION' | 'BOTH') =>
    mode === 'BOTH' ? (['ORIGIN', 'DESTINATION'] as const) : ([mode] as const);

  const createAndAssignService = async (args: {
    name: string;
    code: string;
    scopeMode: 'ORIGIN' | 'DESTINATION' | 'BOTH';
    airstripCodes: string[];
    price: number;
  }) => {
    const scopes = scopeTargets(args.scopeMode);
    await Promise.all(
      scopes.map(async scope => {
        const code = scopes.length === 2 ? `${args.code}_${scope === 'ORIGIN' ? 'DEP' : 'ARR'}` : args.code;
        const name = scopes.length === 2 ? `${args.name} (${scope === 'ORIGIN' ? 'Departure' : 'Destination'})` : args.name;
        const created = await extraServicesApi.createDefinition({
          code,
          name,
          scope,
          pricingModel: 'FIXED',
          defaultPrice: args.price,
          defaultUnit: 1,
          active: true,
          sortOrder: 0,
        });
        await extraServicesApi.bulkUpsertAirstripConfig({
          serviceId: created.id,
          airstripCodes: args.airstripCodes,
          enabled: true,
          priceOverride: args.price,
        });
      })
    );
  };

  const handleCreateGlobalService = async () => {
    const name = globalServiceName.trim();
    const code = toCode(globalServiceCode || globalServiceName);
    const price = Number(globalServicePrice || 0);
    if (!name || !code) {
      alert('Service name/code is required.');
      return;
    }
    if (!globalSelectedAirstripCodes.length) {
      alert('Select at least one airstrip.');
      return;
    }
    setCreatingGlobalService(true);
    try {
      await createAndAssignService({
        name,
        code,
        scopeMode: globalServiceScopeMode,
        airstripCodes: globalSelectedAirstripCodes,
        price,
      });
      setShowGlobalServiceModal(false);
      setGlobalServiceName('');
      setGlobalServiceCode('');
      setGlobalServicePrice('0');
      setGlobalSelectedAirstripCodes([]);
      setGlobalAirstripSearch('');
      alert('Extra service created and assigned.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to create global extra service.');
    } finally {
      setCreatingGlobalService(false);
    }
  };

  const handleCreateManualService = async () => {
    if (!editingAirstrip?.code) return;
    const name = manualServiceName.trim();
    const code = toCode(manualServiceCode || manualServiceName);
    const price = Number(manualServicePrice || 0);
    if (!name || !code) {
      alert('Service name/code is required.');
      return;
    }
    setSavingServicePrices(true);
    try {
      await createAndAssignService({
        name,
        code,
        scopeMode: manualServiceScopeMode,
        airstripCodes: [editingAirstrip.code],
        price,
      });
      const [definitions, configs] = await Promise.all([
        extraServicesApi.listDefinitions(true),
        extraServicesApi.listAirstripConfigs([editingAirstrip.code]),
      ]);
      setServiceDefinitions(definitions.filter(item => item.scope === 'ORIGIN' || item.scope === 'DESTINATION'));
      const map: Record<string, string> = {};
      configs.forEach(cfg => {
        map[cfg.serviceId] =
          cfg.priceOverride === null || cfg.priceOverride === undefined ? '' : String(cfg.priceOverride);
      });
      setServicePriceById(map);
      setManualServiceName('');
      setManualServiceCode('');
      setManualServicePrice('0');
      alert('Service added to this airstrip.');
    } catch (error: any) {
      alert(error?.response?.data?.error || 'Failed to add manual service.');
    } finally {
      setSavingServicePrices(false);
    }
  };

  return (
  <>
    <section className="bg-white rounded-2xl border-2 border-slate-200/50 shadow-xl overflow-hidden backdrop-blur-sm bg-gradient-to-br from-white via-blue-50/20 to-white">
      <div className="px-8 py-6 border-b-2 border-slate-200/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-blue-50/40 via-indigo-50/20 to-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">Airstrip Registry</h3>
            <p className="text-sm text-slate-600 mt-0.5 font-medium">Global Fee & Tax Matrix</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex-1 sm:flex-none">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code, ICAO, name, country"
              className="w-full sm:w-72 px-4 py-2.5 rounded-xl border-2 border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </div>
          <button
            onClick={onDownloadTemplate}
            className="px-4 py-2.5 bg-white text-slate-700 rounded-xl text-sm font-semibold border-2 border-slate-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition-all flex items-center gap-2 shadow-sm hover:shadow-md"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Template
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
            className="px-4 py-2.5 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl text-sm font-semibold hover:from-slate-800 hover:to-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            {isImporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Importing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Import CSV
              </>
            )}
          </button>
          <button
            onClick={() => onOpenModal()}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-blue-700 hover:via-blue-700 hover:to-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-500/50 transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            New Port
          </button>
          <button
            onClick={() => setShowGlobalServiceModal(true)}
            className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-bold hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/40"
          >
            + Add Extra Service
          </button>
          <button
            onClick={onDeleteAll}
            className="px-4 py-2.5 bg-rose-50 text-rose-700 rounded-xl text-sm font-semibold border-2 border-rose-200 hover:bg-rose-100 hover:border-rose-300 transition-all"
          >
            Delete All
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) {
                onImportFile(file);
              }
              event.currentTarget.value = '';
            }}
          />
        </div>
      </div>
      {filteredAirstrips.length === 0 ? (
        <div className="p-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-100 to-indigo-100 mb-4 shadow-lg">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-slate-800 mb-2">No airstrips registered</p>
          <p className="text-sm text-slate-600">Get started by registering your first port</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gradient-to-r from-slate-50 via-blue-50/30 to-slate-50 text-slate-700 text-xs font-bold border-b-2 border-slate-200/50">
              <tr>
                <th className="px-6 py-4">Port Code</th>
                <th className="px-6 py-4">Landing Fee</th>
                <th className="px-6 py-4">Departure Tax</th>
                <th className="px-6 py-4">Immigration</th>
                <th className="px-6 py-4">Fuel</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/50">
              {filteredAirstrips.map((as, idx) => (
                <tr key={as.code} className="hover:bg-gradient-to-r hover:from-blue-50/30 hover:via-indigo-50/20 hover:to-transparent transition-all group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-blue-500/30">
                        {as.code}
                      </div>
                      <div>
                        <span className="text-sm text-slate-900 font-semibold block">{as.name}</span>
                        {as.icao && (
                          <span className="text-xs text-slate-500 font-medium">{as.icao}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-semibold text-slate-800">${Number(as.landingFee || 0).toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-semibold text-slate-800">${Number(as.paxTax || 0).toFixed(2)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${
                      as.isBorderPost 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm' 
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {as.isBorderPost ? '✓ Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${
                      as.fuelAvailable 
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm' 
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {as.fuelAvailable ? '✓ Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={() => onOpenModal(as)}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold hover:bg-blue-100 hover:text-blue-800 transition-all border border-blue-200 shadow-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete airstrip ${as.code}? This action cannot be undone.`)) {
                            onDelete(as.code);
                          }
                        }}
                        className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 hover:text-red-800 transition-all border border-red-200 shadow-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>

    {showAirstripModal && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
        <div className="bg-white rounded-2xl border-2 border-slate-300 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95">
          <div className="bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 text-white px-8 py-6 rounded-t-2xl flex justify-between items-center sticky top-0 z-10 shadow-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Port Registration</h2>
                <p className="text-sm text-blue-100 mt-0.5 font-medium">Registry Expansion Protocol</p>
              </div>
            </div>
            <button
              onClick={onCloseModal}
              className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-8 bg-gradient-to-br from-white via-blue-50/10 to-white">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Port Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={airstripForm.code || ''}
                    onChange={(e) => setAirstripForm({ ...airstripForm, code: e.target.value.toUpperCase() })}
                    placeholder="E.G. ARK"
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                    disabled={!!editingAirstrip}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    ICAO Code
                  </label>
                  <input
                    type="text"
                    value={airstripForm.icao || ''}
                    onChange={(e) => setAirstripForm({ ...airstripForm, icao: e.target.value.toUpperCase() })}
                    placeholder="E.G. HTAR"
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={airstripForm.name || ''}
                    onChange={(e) => setAirstripForm({ ...airstripForm, name: e.target.value })}
                    placeholder="Airstrip Name"
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={airstripForm.country || ''}
                    onChange={(e) => setAirstripForm({ ...airstripForm, country: e.target.value })}
                    placeholder="Country Name"
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={airstripForm.latitude || 0}
                      onChange={(e) => setAirstripForm({ ...airstripForm, latitude: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="0.000001"
                      value={airstripForm.longitude || 0}
                      onChange={(e) => setAirstripForm({ ...airstripForm, longitude: parseFloat(e.target.value) || 0 })}
                      className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 mb-3">Reference Data</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Management</label>
                      <input
                        type="text"
                        value={airstripForm.management || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, management: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">GPS Code</label>
                      <input
                        type="text"
                        value={airstripForm.gpsCode || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, gpsCode: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Runway</label>
                      <input
                        type="text"
                        value={airstripForm.runway || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, runway: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Runway Length (m)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={airstripForm.runwayLengthM || 0}
                        onChange={(e) => setAirstripForm({ ...airstripForm, runwayLengthM: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Surface</label>
                      <input
                        type="text"
                        value={airstripForm.surfaceType || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, surfaceType: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Elevation (ft)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={airstripForm.elevationFt || 0}
                        onChange={(e) => setAirstripForm({ ...airstripForm, elevationFt: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">NMS/DAR</label>
                      <input
                        type="text"
                        value={airstripForm.nmsDar || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, nmsDar: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">BRG/DAR</label>
                      <input
                        type="text"
                        value={airstripForm.brgDar || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, brgDar: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Status</label>
                      <input
                        type="text"
                        value={airstripForm.status || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, status: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-2">Time Zone Index</label>
                      <input
                        type="text"
                        value={airstripForm.timeZoneIndex || ''}
                        onChange={(e) => setAirstripForm({ ...airstripForm, timeZoneIndex: e.target.value })}
                        className="w-full bg-white border-2 border-slate-300 rounded-xl px-3 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500">Port Fees</h4>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Landing Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.landingFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, landingFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Handling Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.handlingFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, handlingFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Handling Fee (International) ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).internationalHandlingFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, internationalHandlingFee: parseFloat(e.target.value) || 0 } as any)}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Navigation Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.navigationFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, navigationFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 pt-2">Passenger Taxes</h4>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Departure Tax ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.paxTax || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, paxTax: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Departure Tax (International) ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.paxTaxInternational || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, paxTaxInternational: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 pt-2">Safety & Security</h4>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Safety & Security (Domestic) ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).safetySecurityDomesticFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, safetySecurityDomesticFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Safety & Security (International) ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).safetySecurityInternationalFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, safetySecurityInternationalFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 pt-2">Aircraft Tax Components</h4>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Pax Bus & Luggage (Per Pax) ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).paxBusLuggageFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, paxBusLuggageFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Aircraft Permit ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).aircraftPermitFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, aircraftPermitFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Clearance ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).clearanceFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, clearanceFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Repositioning ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={(airstripForm as any).repositionFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, repositionFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500 pt-2">Arrival & Ancillary</h4>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Arrival Tax ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.arrivalTax || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, arrivalTax: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Meet & Assist Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.meetAndAssistFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, meetAndAssistFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Lounge Fee ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.loungeFee || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, loungeFee: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t-2 border-slate-200/50">
              <div className="flex items-center gap-3 mb-4 p-4 rounded-xl bg-slate-50/50 hover:bg-blue-50/50 transition-colors">
                <input
                  type="checkbox"
                  id="immigrationOffice"
                  checked={airstripForm.isBorderPost || false}
                  onChange={(e) => setAirstripForm({ ...airstripForm, isBorderPost: e.target.checked })}
                  className="w-5 h-5 rounded-lg border-2 border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="immigrationOffice" className="text-sm font-semibold text-slate-700 cursor-pointer">
                  Immigration Office Available
                </label>
              </div>

              <div className="flex items-center gap-3 mb-4 p-4 rounded-xl bg-slate-50/50 hover:bg-blue-50/50 transition-colors">
                <input
                  type="checkbox"
                  id="fuelAvailable"
                  checked={airstripForm.fuelAvailable || false}
                  onChange={(e) => setAirstripForm({ ...airstripForm, fuelAvailable: e.target.checked })}
                  className="w-5 h-5 rounded-lg border-2 border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="fuelAvailable" className="text-sm font-semibold text-slate-700 cursor-pointer">
                  Authorize for Fuel Supply Network
                </label>
              </div>

              {airstripForm.fuelAvailable && (
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Fuel Price per Liter ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={airstripForm.fuelPricePerLiter || 0}
                    onChange={(e) => setAirstripForm({ ...airstripForm, fuelPricePerLiter: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>
              )}
            </div>

            {editingAirstrip?.code && (
              <div className="mt-6 pt-6 border-t-2 border-slate-200/50 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-black uppercase tracking-[0.3em] text-slate-500">Extra Services Pricing</h4>
                  <button
                    type="button"
                    onClick={saveAirstripServicePricing}
                    disabled={savingServicePrices}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-900 disabled:opacity-50"
                  >
                    {savingServicePrices ? 'Saving...' : 'Save Services'}
                  </button>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600">
                    Add New Service For This Airstrip
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <input
                      value={manualServiceName}
                      onChange={e => setManualServiceName(e.target.value)}
                      placeholder="Service name"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <input
                      value={manualServiceCode}
                      onChange={e => setManualServiceCode(e.target.value.toUpperCase())}
                      placeholder="Service code (optional)"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                    <select
                      value={manualServiceScopeMode}
                      onChange={e => setManualServiceScopeMode(e.target.value as 'ORIGIN' | 'DESTINATION' | 'BOTH')}
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="BOTH">Both Departure + Destination</option>
                      <option value="ORIGIN">Departure Only</option>
                      <option value="DESTINATION">Destination Only</option>
                    </select>
                    <input
                      type="number"
                      step="0.01"
                      value={manualServicePrice}
                      onChange={e => setManualServicePrice(e.target.value)}
                      placeholder="Price"
                      className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleCreateManualService}
                    disabled={savingServicePrices}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Add Service To This Airstrip
                  </button>
                </div>
                {serviceDefinitions.length === 0 ? (
                  <p className="text-xs text-slate-500">No service definitions found. Add services in the Services tab.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {serviceDefinitions.map(service => (
                      <div key={service.id}>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          {service.name} ({service.scope})
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={servicePriceById[service.id] ?? ''}
                          onChange={e =>
                            setServicePriceById(prev => ({
                              ...prev,
                              [service.id]: e.target.value,
                            }))
                          }
                          placeholder={`Default ${service.defaultPrice}`}
                          className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all shadow-sm hover:shadow-md"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 flex justify-end gap-4 pt-6 border-t-2 border-slate-200/50">
              <button
                onClick={onCloseModal}
                className="px-6 py-3 bg-white text-slate-700 rounded-xl text-sm font-semibold border-2 border-slate-300 hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm hover:shadow-md"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isSavingAirstrip}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-500/50 flex items-center gap-2 transform hover:scale-105"
              >
                {isSavingAirstrip ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : editingAirstrip ? (
                  'Update Port'
                ) : (
                  'Create Port'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {showGlobalServiceModal && (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-3xl rounded-2xl border-2 border-slate-300 bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-slate-900">Create Extra Service</h3>
              <p className="text-sm text-slate-500">Create once, assign to one or many airstrips</p>
            </div>
            <button
              type="button"
              onClick={() => setShowGlobalServiceModal(false)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700"
            >
              Close
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                value={globalServiceName}
                onChange={e => setGlobalServiceName(e.target.value)}
                placeholder="Service Name"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <input
                value={globalServiceCode}
                onChange={e => setGlobalServiceCode(e.target.value.toUpperCase())}
                placeholder="Service Code (optional)"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
              <select
                value={globalServiceScopeMode}
                onChange={e => setGlobalServiceScopeMode(e.target.value as 'ORIGIN' | 'DESTINATION' | 'BOTH')}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="BOTH">Both Departure + Destination</option>
                <option value="ORIGIN">Departure Only</option>
                <option value="DESTINATION">Destination Only</option>
              </select>
              <input
                type="number"
                step="0.01"
                value={globalServicePrice}
                onChange={e => setGlobalServicePrice(e.target.value)}
                placeholder="Price"
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="rounded-xl border border-slate-200 p-3 max-h-[320px] overflow-auto">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Select Airstrips</div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setGlobalSelectedAirstripCodes(prev => Array.from(new Set([...prev, ...globalFilteredAirstrips.map(a => a.code)])))
                    }
                    className="px-2 py-1 rounded border border-slate-300 text-[11px] font-semibold text-slate-700"
                  >
                    Select Filtered
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setGlobalSelectedAirstripCodes(prev => prev.filter(code => !globalFilteredAirstrips.some(a => a.code === code)))
                    }
                    className="px-2 py-1 rounded border border-slate-300 text-[11px] font-semibold text-slate-700"
                  >
                    Clear Filtered
                  </button>
                </div>
              </div>
              <input
                value={globalAirstripSearch}
                onChange={e => setGlobalAirstripSearch(e.target.value)}
                placeholder="Type code/name/country to filter airstrips..."
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-2"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {globalFilteredAirstrips.map(as => {
                  const checked = globalSelectedAirstripCodes.includes(as.code);
                  return (
                    <label key={as.code} className="flex items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          if (e.target.checked) {
                            setGlobalSelectedAirstripCodes(prev => [...prev, as.code]);
                          } else {
                            setGlobalSelectedAirstripCodes(prev => prev.filter(code => code !== as.code));
                          }
                        }}
                      />
                      <span>{as.code} - {as.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowGlobalServiceModal(false)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateGlobalService}
                disabled={creatingGlobalService}
                className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {creatingGlobalService ? 'Creating...' : 'Create & Assign'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {importSummary && (
      <AirstripImportSummaryModal summary={importSummary} onClose={onCloseImportSummary} />
    )}
  </>
  );
};
